import { Requests } from "../request/Requests";
import { Maybe } from "../util/util";
import { Caching } from "../cache/Caching";

//TODO: cache stuff
/** Resolves player names, UUIDs, and service IDs to skin or cape texture URLs. */
export class Skins {


    //<editor-fold desc="Skins">

    /** Returns a skin URL for {@link MineRenderScene.addSkin}, or `undefined` if name lookup fails. */
    static async fromUuidOrUsername(uuidOrUsername: string): Promise<Maybe<string>> {
        if (uuidOrUsername.length === 32 || uuidOrUsername.length === 36) {
            return this.fromUuid(uuidOrUsername);
        }
        return this.fromUsername(uuidOrUsername);
    }

    /** Builds a proxy URL for a UUID without downloading or checking the skin. */
    static async fromUuid(uuid: string): Promise<Maybe<string>> {
        return this.getMcProxySkinUrl(uuid);
    }

    static async fromUsername(username: string): Promise<Maybe<string>> {
        return this.usernameToUuid(username).then(uuid => {
            if (uuid) {
                return this.fromUuid(uuid);
            }
            return undefined;
        })
    }

    /** Looks up a saved MineSkin skin and returns its texture URL, or `undefined` on lookup failure. */
    static async fromMineSkin(mineskinId: string): Promise<Maybe<string>> {
        return this.getMineSkin(mineskinId).then(mineskin => {
            if (mineskin) {
                return mineskin["data"]["texture"]["value"]["urls"]["skins"];
            }
            return undefined;
        })
    }

    //</editor-fold>


    //<editor-fold desc="Capes">

    /** Resolves a player's Minecraft cape URL for {@link SkinObject.setCapeTexture}. */
    static async capeFromUuidOrUsername(uuidOrUsername: string): Promise<Maybe<string>> {
        if (uuidOrUsername.length === 32 || uuidOrUsername.length === 36) {
            return this.capeFromUuid(uuidOrUsername);
        }
        return this.capeFromUsername(uuidOrUsername);
    }

    /** Builds a proxy URL without checking whether the player has a cape. */
    static async capeFromUuid(uuid: string): Promise<Maybe<string>> {
        return this.getMcProxyCapeUrl(uuid);
    }

    static async capeFromUsername(username: string): Promise<Maybe<string>> {
        return this.usernameToUuid(username).then(uuid => {
            if (uuid) {
                return this.capeFromUuid(uuid);
            }
            return undefined;
        })
    }

    /** Returns a full static cape texture for a username or UUID. */
    static async capeFromCapesDev(player: string, capeType: string = "minecraft"): Promise<Maybe<string>> {
        const cape = await this.getCapesDev(player, capeType);
        return cape?.exists ? cape.imageUrls?.still?.full : undefined;
    }

    //</editor-fold>


    //<editor-fold desc="Helpers">

    private static async usernameToUuid(username: string): Promise<Maybe<string>> {
        return Caching.usernameUuidCache.get(username, () => this.usernameToUuidMcProxy(username));
    }

    private static async usernameToUuidMcProxy(username: string): Promise<Maybe<string>> {
        return Requests.genericRequest({
            baseURL: 'https://mcproxy.dev',
            url: '/uuid/' + username
        }).then(res => {
            if (res.status === 200) {
                return res.data["data"]["id"];
            }
            return undefined;
        }).catch(err => {
            console.warn(err);
            return undefined;
        })
    }

    /** @deprecated **/
    private static async usernameToUuidSkinProxy(username: string): Promise<Maybe<string>> {
        return Requests.genericRequest({
            baseURL: 'https://minecraft-skin-proxy.inventive.workers.dev',
            url: '/uuid/' + username
        }).then(res => {
            if (res.status === 200) {
                return res.data["id"];
            }
            return undefined;
        }).catch(err => {
            console.warn(err);
            return undefined;
        })
    }

    private static async usernameToUuidMineTools(username: string): Promise<Maybe<string>> {
        return Requests.genericRequest({
            baseURL: 'https://api.minetools.eu',
            url: '/uuid/' + username
        }).then(res => {
            if (res.status === 200) {
                return res.data["id"];
            }
            return undefined;
        }).catch(err => {
            console.warn(err);
            return undefined;
        })
    }

    private static async getProfileFromMinetools(uuid: string): Promise<Maybe<any>> {
        return Requests.genericRequest({
            baseURL: 'https://api.minetools.eu',
            url: '/profile/' + uuid
        }).then(res => {
            if (res.status === 200) {
                return res.data;
            }
        })
    }

    private static async getMineSkin(mineskinId: string): Promise<Maybe<any>> {
        return Requests.genericRequest({
            baseURL: 'https://api.mineskin.org',
            url: '/get/uuid/' + mineskinId
        }).then(res => {
            if (res.status === 200) {
                return res.data;
            }
            return undefined;
        }).catch(err => {
            console.warn(err);
            return undefined;
        })
    }

    private static async getCapesDev(player: string, type: string = 'all'): Promise<Maybe<any>> {
        return Requests.genericRequest({
            baseURL: 'https://api.capes.dev',
            url: '/load/' + encodeURIComponent(player) + '/' + encodeURIComponent(type)
        }).then(res => {
            if (res.status === 200) {
                return res.data;
            }
            return undefined;
        })
    }

    private static getCrafatarSkinUrl(uuid: string): string {
        return "https://crafatar.com/skins/" + uuid;
    }

    private static getCrafatarCapeUrl(uuid: string): string {
        return "https://crafatar.com/capes/" + uuid;
    }

    private static getMcHeadsSkinUrl(uuid: string): string {
        return "https://mc-heads.net/skin/" + uuid;
    }

    private static getMineSkinPlayerSkinUrl(uuid: string): string {
        return "https://mineskin.org/skin/player/" + uuid;
    }

    /** @deprecated **/
    private static getSkinProxySkinUrl(uuid: string): string {
        return "https://minecraft-skin-proxy.inventive.workers.dev/skin/" + uuid;
    }

    /** @deprecated **/
    private static getSkinProxyCapeUrl(uuid: string): string {
        return "https://minecraft-skin-proxy.inventive.workers.dev/cape/" + uuid;
    }

    private static getMcProxySkinUrl(uuid: string): string {
        return "https://mcproxy.dev/skin/" + uuid;
    }

    private static getMcProxyCapeUrl(uuid: string): string {
        return "https://mcproxy.dev/cape/" + uuid;
    }

    //</editor-fold>


}
